import { CurrencyPipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { IChartData } from '@core/interfaces/components/chart.interface';
import { DeviceService } from '@shared/services/device.service';
import { Chart, ChartConfiguration, registerables } from 'chart.js';

Chart.register(...registerables);

@Component({
  selector: 'app-card-chart-1',
  imports: [CurrencyPipe, NgClass],
  templateUrl: './card-chart-1.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './card-chart-1.css',
})
export class CardChart1 implements AfterViewInit, OnDestroy {
  private readonly deviceService = inject(DeviceService);
  private readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('chartCanvas');

  type = input.required<
    'bar' | 'line' | 'scatter' | 'bubble' | 'pie' | 'doughnut' | 'polarArea' | 'radar'
  >();
  titulo = input.required<string>();
  data = input.required<IChartData>();
  tipoData = input.required<'dinero' | 'cantidad'>();
  inputBasicOptions = input<any | undefined>(undefined);
  showLegend = input<boolean>(true);

  private chartInstance: Chart | null = null;

  ngAfterViewInit(): void {
    if (!this.deviceService.isBrowser()) return;
    this.createChart();
  }

  ngOnDestroy(): void {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  }

  private createChart(): void {
    if (!this.data().datasets.length) return;

    if (this.chartInstance) {
      this.chartInstance.destroy();
    }

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color') || '#374151';
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary') || '#6b7280';
    const surfaceBorder = documentStyle.getPropertyValue('--border-color') || '#d1d5db';

    const defaultOpts: ChartConfiguration['options'] = {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        legend: {
          display: this.showLegend(),
          labels: { color: textColor },
        },
      },
      scales: {
        x: {
          ticks: { color: textColorSecondary },
          grid: { color: surfaceBorder },
        },
        y: {
          beginAtZero: true,
          ticks: { color: textColorSecondary },
          grid: { color: surfaceBorder },
        },
      },
    };

    const opts = this.inputBasicOptions() !== undefined
      ? { ...this.inputBasicOptions() }
      : defaultOpts;

    const config: ChartConfiguration = {
      type: this.type() as any,
      data: {
        labels: this.data().labels || [],
        datasets: (this.data().datasets || []).map((dataset: any) => ({
          label: dataset.label,
          data: dataset.data,
          backgroundColor: dataset.backgroundColor,
          borderColor: dataset.borderColor,
          borderWidth: dataset.borderWidth || 1,
        })),
      },
      options: opts,
    };

    this.chartInstance = new Chart(this.canvas().nativeElement, config);
  }
}
